import {
  CONTRIBUTIONS_PAGE_SIZE,
  type ContributionsCommit,
  type ContributionsQueryResult,
  type ContributionsRange,
  type EnvironmentId,
  type ProjectId,
} from "@afar-research/contracts";
import { DownloadIcon, FingerprintIcon, RefreshCwIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { saveBlob } from "../../browser/streamedCapture";
import { projectEnvironment } from "../../state/projects";
import { useEnvironmentQuery } from "../../state/query";
import { useAtomCommand } from "../../state/use-atom-command";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { Button } from "../ui/button";
import { ProjectContributionPublishing } from "./ProjectContributionPublishing";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../ui/empty";
import { ScrollArea } from "../ui/scroll-area";
import { stackedThreadToast, toastManager } from "../ui/toast";
import { Toggle, ToggleGroup } from "../ui/toggle-group";
import {
  authorshipShares,
  contributionModelLabel,
  type AuthorshipShares,
} from "./contributionsPanel.logic";

/**
 * Who wrote a thread checkout's code, read from the git-ai Authorship Logs on
 * its commits. Every figure comes from the notes; this panel derives nothing
 * the Environment did not report.
 */

interface ContributionsPanelProps {
  readonly projectId: ProjectId;
  readonly environmentId: EnvironmentId;
  /** The thread's checkout: its Primary Checkout or Thread Worktree. */
  readonly cwd: string | null;
  /** When the thread began, for the "This thread" range. */
  readonly threadCreatedAt: string;
}

const AI_BAR = "bg-violet-500/80 dark:bg-violet-400/80";
const HUMAN_BAR = "bg-emerald-500/80 dark:bg-emerald-400/80";
const UNKNOWN_BAR = "bg-muted-foreground/25";

/** The thread range starts when the thread began; the branch range has no start. */
const rangeScope = (range: ContributionsRange, since: string) =>
  range === "thread" ? { range, since } : { range };

function contributionsQuery(
  environmentId: EnvironmentId,
  cwd: string,
  range: ContributionsRange,
  since: string,
  cursor: number,
) {
  return projectEnvironment.queryContributions({
    environmentId,
    input: { cwd, ...rangeScope(range, since), cursor },
  });
}

function ShareSegments({ shares }: { readonly shares: AuthorshipShares }) {
  return (
    <>
      <span className={AI_BAR} style={{ width: `${shares.ai}%` }} />
      <span className={HUMAN_BAR} style={{ width: `${shares.human}%` }} />
      <span className={UNKNOWN_BAR} style={{ width: `${shares.unknown}%` }} />
    </>
  );
}

function ShareBar({ summary }: { readonly summary: ContributionsQueryResult["summary"] }) {
  const shares = authorshipShares(summary);
  const legend = [
    { label: "Agents", share: shares.ai, lines: summary.aiLines, swatch: AI_BAR },
    { label: "You", share: shares.human, lines: summary.humanLines, swatch: HUMAN_BAR },
    { label: "Untracked", share: shares.unknown, lines: summary.unknownLines, swatch: UNKNOWN_BAR },
  ];
  return (
    <div className="flex flex-col gap-2">
      <div
        aria-hidden
        className="flex h-2 w-full overflow-hidden rounded-full bg-muted"
        title={`${shares.totalLines.toLocaleString()} lines added`}
      >
        <ShareSegments shares={shares} />
      </div>
      <dl className="grid grid-cols-3 gap-2 text-xs">
        {legend.map((entry) => (
          <div key={entry.label} className="flex min-w-0 flex-col gap-0.5">
            <dt className="flex items-center gap-1.5 text-muted-foreground">
              <span aria-hidden className={`size-2 shrink-0 rounded-full ${entry.swatch}`} />
              {entry.label}
            </dt>
            <dd className="font-medium tabular-nums">
              {entry.share}%{" "}
              <span className="font-normal text-muted-foreground">
                {entry.lines.toLocaleString()} lines
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function CommitRow({ commit }: { readonly commit: ContributionsCommit }) {
  const shares = authorshipShares(commit);
  return (
    <li className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent/60">
      <span className="shrink-0 font-mono text-xs text-muted-foreground">
        {commit.sha.slice(0, 7)}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm" title={commit.subject}>
        {commit.subject}
      </span>
      {commit.hasAuthorshipLog ? (
        <span
          className="flex h-1.5 w-12 shrink-0 overflow-hidden rounded-full bg-muted"
          title={`Agents ${shares.ai}% · You ${shares.human}% · Untracked ${shares.unknown}%`}
        >
          <ShareSegments shares={shares} />
        </span>
      ) : (
        <span className="w-12 shrink-0 text-right text-[11px] text-muted-foreground">No log</span>
      )}
      <span className="w-16 shrink-0 text-right text-[11px] text-muted-foreground">
        {formatRelativeTimeLabel(commit.committedAt)}
      </span>
    </li>
  );
}

/** One page of commits; each page is its own cached read, so loading more never re-reads the first. */
function CommitPage({
  environmentId,
  cwd,
  range,
  since,
  cursor,
  isLast,
  onLoadMore,
}: {
  readonly environmentId: EnvironmentId;
  readonly cwd: string;
  readonly range: ContributionsRange;
  readonly since: string;
  readonly cursor: number;
  readonly isLast: boolean;
  readonly onLoadMore: () => void;
}) {
  const query = useEnvironmentQuery(contributionsQuery(environmentId, cwd, range, since, cursor));
  if (query.data === null) return null;
  return (
    <>
      {query.data.commits.map((commit) => (
        <CommitRow key={commit.sha} commit={commit} />
      ))}
      {isLast && query.data.nextCursor !== null ? (
        <li className="px-2 py-1">
          <Button size="xs" variant="ghost" onClick={onLoadMore}>
            Show more commits
          </Button>
        </li>
      ) : null}
    </>
  );
}

export function ContributionsPanel({
  projectId,
  environmentId,
  cwd,
  threadCreatedAt,
}: ContributionsPanelProps) {
  const [range, setRange] = useState<ContributionsRange>("branch");
  const [pageCount, setPageCount] = useState(1);
  const query = useEnvironmentQuery(
    cwd === null ? null : contributionsQuery(environmentId, cwd, range, threadCreatedAt, 0),
  );
  const exportAgentTrace = useAtomCommand(projectEnvironment.exportAgentTrace);
  const [exporting, setExporting] = useState(false);

  const changeRange = useCallback((next: ContributionsRange) => {
    setRange(next);
    setPageCount(1);
  }, []);

  const onExport = useCallback(async () => {
    if (cwd === null) return;
    setExporting(true);
    const result = await exportAgentTrace({
      environmentId,
      input: { cwd, ...rangeScope(range, threadCreatedAt) },
    });
    setExporting(false);
    if (result._tag === "Failure") return;
    if (result.value.recordCount === 0) {
      toastManager.add(
        stackedThreadToast({
          type: "info",
          title: "Nothing to export",
          description: "No commit in this range has an Authorship Log yet.",
        }),
      );
      return;
    }
    saveBlob(
      new Blob([result.value.content], { type: "application/jsonl" }),
      result.value.fileName,
    );
  }, [cwd, environmentId, exportAgentTrace, range, threadCreatedAt]);

  const pages = useMemo(() => Array.from({ length: pageCount }, (_, index) => index), [pageCount]);

  if (cwd === null) {
    return (
      <Empty className="justify-center-safe px-4 py-16" role="status">
        <EmptyHeader>
          <EmptyTitle>No checkout</EmptyTitle>
          <EmptyDescription>
            This thread has no checkout to read contributions from.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  if (query.error !== null && query.data === null) {
    return (
      <Empty className="justify-center-safe px-4 py-16" role="alert">
        <EmptyMedia variant="icon">
          <FingerprintIcon />
        </EmptyMedia>
        <EmptyHeader>
          <EmptyTitle>Could not read contributions</EmptyTitle>
          <EmptyDescription>{query.error}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button size="sm" variant="outline" onClick={query.refresh}>
            <RefreshCwIcon />
            Try again
          </Button>
        </EmptyContent>
      </Empty>
    );
  }

  const data = query.data;
  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col gap-4 p-3">
        <ProjectContributionPublishing environmentId={environmentId} projectId={projectId} />
        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup
            aria-label="Commits to count"
            onValueChange={(value) => {
              const next = value[0];
              if (next === "branch" || next === "thread") changeRange(next);
            }}
            value={[range]}
            variant="segmented"
          >
            <Toggle value="branch">Whole branch</Toggle>
            <Toggle value="thread">This thread</Toggle>
          </ToggleGroup>
          <Button
            className="ml-auto"
            disabled={exporting || data === null || data.summary.commitsWithAuthorshipLog === 0}
            size="xs"
            variant="outline"
            onClick={() => void onExport()}
          >
            <DownloadIcon />
            Export Agent Trace…
          </Button>
        </div>

        {data === null ? (
          <p className="text-sm text-muted-foreground">Reading Authorship Logs…</p>
        ) : (
          <>
            {data.tracking === "unavailable" ? (
              <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-xs">
                This Environment is not recording new contributions. Existing Authorship Logs are
                still shown.
              </p>
            ) : null}

            {data.summary.commitCount === 0 ? (
              <p className="text-sm text-muted-foreground">No commits in this range yet.</p>
            ) : (
              <>
                <ShareBar summary={data.summary} />
                <p className="text-xs text-muted-foreground">
                  {data.summary.commitsWithAuthorshipLog.toLocaleString()} of{" "}
                  {data.summary.newestOnly ? "the newest " : ""}
                  {data.summary.commitCount.toLocaleString()} commits have an Authorship Log.
                </p>
                {data.summary.byModel.length > 0 ? (
                  <ul className="flex flex-col gap-1 text-xs">
                    {data.summary.byModel.map((entry) => (
                      <li
                        key={`${entry.tool}:${entry.model}`}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{contributionModelLabel(entry)}</span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          {entry.aiLines.toLocaleString()} lines
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <ul className="-mx-2 flex flex-col">
                  {pages.map((page) => (
                    <CommitPage
                      key={`${range}:${page}`}
                      environmentId={environmentId}
                      cwd={cwd}
                      range={range}
                      since={threadCreatedAt}
                      cursor={page * CONTRIBUTIONS_PAGE_SIZE}
                      isLast={page === pages.length - 1}
                      onLoadMore={() => setPageCount((count) => count + 1)}
                    />
                  ))}
                </ul>
              </>
            )}

            <ul className="flex list-disc flex-col gap-1 pl-4 text-[11px] text-muted-foreground">
              <li>Lines are counted when they are committed, from each commit's Authorship Log.</li>
              <li>Edits you make while an agent is working count as the agent's.</li>
              <li>
                Lines changed between turns count as yours, whichever tool made them. Lines no log
                covers are untracked and credited to nobody.
              </li>
            </ul>
          </>
        )}
      </div>
    </ScrollArea>
  );
}
